/**
 * Hook for fetching and managing page content
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchContent } from "@/api/content";
import type { BlockGridContentResponse } from "@/api/content";

interface UseContentResult {
  content: BlockGridContentResponse | null;
  isLoading: boolean;
  error: string | null;
  redirectUrl: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch page content from Umbraco CMS
 * @param url - The page URL path (e.g., "/", "/about")
 * @param options.previewId - Optional Umbraco content node ID for preview (fetches drafts)
 */
export function useContent(
  url: string,
  options?: { previewId?: string }
): UseContentResult {
  const previewId = options?.previewId;
  const [content, setContent] = useState<BlockGridContentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    setRedirectUrl(null);

    const result = await fetchContent(url, controller.signal, { previewId });

    if (controller.signal.aborted) return;

    if (result.ok) {
      setContent(result.data);
    } else {
      setError(result.error);
      setRedirectUrl(result.redirectUrl ?? null);
    }

    setIsLoading(false);
  }, [url, previewId]);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    content,
    isLoading,
    error,
    redirectUrl,
    refetch: load,
  };
}
